import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowUpDown,
  ChevronDown,
  Clock,
  Filter,
  Loader2,
  Mail,
  MapPin,
  Package,
  Phone,
  RefreshCw,
  Search,
  SearchX,
  Store,
  X,
} from "lucide-react";
import MenusPagination from "./MenusPagination.jsx";

const STORAGE_PATH = "/storage";
const DEFAULT_ASSET_BASE_URL = `https://snadmin.enrichinsights.com${STORAGE_PATH}`;

const getAssetBaseUrl = () => {
  const baseUrl = process.env.REACT_APP_ASSET_BASE_URL;

  if (baseUrl) {
    return baseUrl.replace(/\/+$/, "");
  }

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  if (apiBaseUrl?.startsWith("http")) {
    return `${apiBaseUrl.replace(/\/+$/, "").replace(/\/api\/?$/, "")}${STORAGE_PATH}`;
  }

  return DEFAULT_ASSET_BASE_URL;
};

const getImageUrl = (path) => {
  if (!path) {
    return "";
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${getAssetBaseUrl()}/${path.replace(/^\//, "")}`;
};

const formatPrice = (price, currency) => {
  if (price === null || price === undefined || price === "") {
    return "-";
  }

  const amount = Number(price);
  const displayPrice = Number.isFinite(amount)
    ? amount.toLocaleString("en-US")
    : price;

  return `${displayPrice} ${currency || "MMK"}`;
};

const stripHtml = (value) => {
  if (!value) {
    return "";
  }

  return value.replace(/<br\s*\/?>/gi, ", ").replace(/<[^>]*>/g, "");
};

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const getTodayHours = (openingHours) => {
  if (!openingHours) {
    return "";
  }

  const today = DAY_NAMES[new Date().getDay()];
  const segments = String(openingHours).split(",");
  const match = segments.find((segment) =>
    segment.trim().toLowerCase().startsWith(today.toLowerCase()),
  );

  if (!match) {
    return "";
  }

  return match.trim().slice(today.length).trim();
};

const MenuSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
    {Array.from({ length: 8 }).map((_, index) => (
      <div
        key={index}
        className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm"
      >
        <div className="h-48 bg-gray-100 animate-pulse" />
        <div className="p-4 space-y-3">
          <div className="h-5 bg-gray-100 rounded animate-pulse" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-4/5" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-2/5" />
        </div>
      </div>
    ))}
  </div>
);

const MerchantBanner = ({ branch, merchant, itemCount }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = getImageUrl(merchant?.logo) || getImageUrl(branch?.image_url);

  const branchName = branch?.name || merchant?.name || "Merchant";
  const phone = branch?.phone || merchant?.phone;
  const email = branch?.email || merchant?.email;
  const address = branch?.address || "";
  const todayHours = getTodayHours(branch?.opening_hours);
  const showMerchantLine = branch?.name && merchant?.name && branch.name !== merchant.name;

  return (
    <section className="bg-primary-color text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-white/10 border border-white/20">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt={merchant?.name || "Merchant"}
                className="w-full h-full object-cover"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Store className="w-10 h-10 text-white/80" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-secondary-color font-semibold mb-2">
              <Store className="w-4 h-4" />
              <span>Menu</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-playfair break-words">
              {branchName}
            </h1>
            {showMerchantLine && (
              <p className="mt-1 text-sm text-white/70">
                {merchant.name}
              </p>
            )}
            {address && (
              <p className="mt-3 max-w-3xl flex items-start gap-2 text-white/80">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{address}</span>
              </p>
            )}
            {!address && merchant?.description && (
              <p className="mt-3 max-w-3xl text-white/80">
                {merchant.description}
              </p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-white/85">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-md px-3 py-2">
                <Package className="w-4 h-4" />
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </span>
              {phone && (
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-md px-3 py-2">
                  <Phone className="w-4 h-4" />
                  {phone}
                </span>
              )}
              {todayHours && (
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-md px-3 py-2">
                  <Clock className="w-4 h-4" />
                  Today {todayHours}
                </span>
              )}
              {email && (
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-md px-3 py-2">
                  <Mail className="w-4 h-4" />
                  {email}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MenuCard = ({ menu }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = getImageUrl(menu.image1_url);
  const isAvailable = Number(menu.is_available) === 1;

  return (
    <article className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="relative h-52 bg-gray-100">
        {imageUrl && !imageFailed ? (
          <img
            src={imageUrl}
            alt={menu.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Package className="w-12 h-12" />
          </div>
        )}
        <span
          className={`absolute top-3 left-3 rounded-md px-3 py-1 text-xs font-semibold ${
            isAvailable
              ? "bg-emerald-600 text-white"
              : "bg-gray-800 text-white"
          }`}
        >
          {isAvailable ? "Available" : "Unavailable"}
        </span>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-gray-950 break-words">
            {menu.name}
          </h2>
          <span className="shrink-0 text-primary-color font-bold">
            {formatPrice(menu.price, menu.currency)}
          </span>
        </div>

        {menu.description && (
          <p className="mt-2 text-sm text-gray-600 line-clamp-3">
            {menu.description}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-600">
          {menu.quantity !== null && menu.quantity !== undefined && (
            <span className="rounded-md bg-gray-100 px-2.5 py-1">
              Qty: {menu.quantity}
              {menu.unit ? ` ${menu.unit}` : ""}
            </span>
          )}
          {stripHtml(menu.tax) && (
            <span className="rounded-md bg-gray-100 px-2.5 py-1">
              {stripHtml(menu.tax)}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

const ITEMS_PER_PAGE = 8;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name_asc", label: "Name: A to Z" },
  { value: "name_desc", label: "Name: Z to A" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
];

const AVAILABILITY_OPTIONS = [
  { value: "", label: "All availability" },
  { value: "available", label: "Available" },
  { value: "unavailable", label: "Unavailable" },
];

const toPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const UNCATEGORIZED_VALUE = "__uncategorized__";

const getCategoryKey = (menu) => {
  const id = menu?.menu_category_id;

  if (id === null || id === undefined || id === "") {
    return "";
  }

  return String(id);
};

const getCategoryLabel = (key, categories, sampleMenu) => {
  if (key === "") {
    return "Uncategorized";
  }

  if (sampleMenu?.category_name) {
    return sampleMenu.category_name;
  }

  const match = (Array.isArray(categories) ? categories : []).find(
    (item) => String(item?.id) === key,
  );

  return match?.name || `Category ${key}`;
};

const Menus = ({
  branchId,
  branch,
  categories = [],
  error,
  isLoading,
  menus,
  merchant,
  onRetry,
}) => {
  const branchLabel = branch?.name || merchant?.name || "Merchant";

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${branchLabel} · Menu`;

    return () => {
      document.title = previousTitle;
    };
  }, [branchLabel]);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [availability, setAvailability] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const moreFiltersRef = useRef(null);

  const hasActiveFilters = Boolean(
    search || category || availability || minPrice || maxPrice,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, category, availability, minPrice, maxPrice, sort]);

  useEffect(() => {
    if (!showMoreFilters) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (
        moreFiltersRef.current &&
        !moreFiltersRef.current.contains(event.target)
      ) {
        setShowMoreFilters(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showMoreFilters]);

  const categoryOptions = useMemo(() => {
    const list = [];
    const seen = new Set();

    (Array.isArray(menus) ? menus : []).forEach((menu) => {
      const key = getCategoryKey(menu);

      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      list.push({ value: key, label: getCategoryLabel(key, categories, menu) });
    });

    return list.sort((a, b) => {
      if (a.value === "") {
        return 1;
      }

      if (b.value === "") {
        return -1;
      }

      return a.label.localeCompare(b.label);
    });
  }, [menus, categories]);

  const selectedCategoryKey =
    category === UNCATEGORIZED_VALUE ? "" : category;

  const filteredMenus = useMemo(() => {
    const list = Array.isArray(menus) ? menus : [];
    const term = search.trim().toLowerCase();
    const min = minPrice === "" ? null : Number(minPrice);
    const max = maxPrice === "" ? null : Number(maxPrice);

    const filtered = list.filter((menu) => {
      const haystack = `${menu?.name || ""} ${menu?.description || ""}`.toLowerCase();

      if (term && !haystack.includes(term)) {
        return false;
      }

      if (category && getCategoryKey(menu) !== selectedCategoryKey) {
        return false;
      }

      if (availability) {
        const isAvailable = Number(menu?.is_available) === 1;
        const matches =
          (availability === "available" && isAvailable) ||
          (availability === "unavailable" && !isAvailable);

        if (!matches) {
          return false;
        }
      }

      if (min !== null || max !== null) {
        const amount = toPrice(menu?.price);

        if (amount === null) {
          return false;
        }

        if (min !== null && amount < min) {
          return false;
        }

        if (max !== null && amount > max) {
          return false;
        }
      }

      return true;
    });

    const sorted = [...filtered];

    switch (sort) {
      case "oldest":
        sorted.sort((a, b) => Number(a.id) - Number(b.id));
        break;
      case "name_asc":
        sorted.sort((a, b) =>
          String(a?.name || "").localeCompare(String(b?.name || "")),
        );
        break;
      case "name_desc":
        sorted.sort((a, b) =>
          String(b?.name || "").localeCompare(String(a?.name || "")),
        );
        break;
      case "price_asc":
        sorted.sort((a, b) => (toPrice(a?.price) ?? 0) - (toPrice(b?.price) ?? 0));
        break;
      case "price_desc":
        sorted.sort((a, b) => (toPrice(b?.price) ?? 0) - (toPrice(a?.price) ?? 0));
        break;
      default:
        sorted.sort((a, b) => Number(b.id) - Number(a.id));
    }

    return sorted;
  }, [menus, search, category, selectedCategoryKey, availability, minPrice, maxPrice, sort]);

  const groupedMenus = useMemo(() => {
    const order = categoryOptions.map((option) => option.value);
    const groups = new Map();

    filteredMenus.forEach((menu) => {
      const key = getCategoryKey(menu);

      if (!groups.has(key)) {
        groups.set(key, []);
      }

      groups.get(key).push(menu);
    });

    return Array.from(groups.entries())
      .map(([key, items]) => ({
        key,
        label: getCategoryLabel(key, categories, items[0]),
        position: order.indexOf(key),
        items,
      }))
      .sort(
        (a, b) =>
          a.position - b.position || a.label.localeCompare(b.label),
      );
  }, [filteredMenus, categoryOptions, categories]);

  const flattenedMenus = useMemo(
    () => groupedMenus.flatMap((group) => group.items),
    [groupedMenus],
  );

  const totalItems = flattenedMenus.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
  const pageItems = flattenedMenus.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );
  const visibleGroups = groupedMenus
    .map((group) => ({
      ...group,
      items: pageItems.filter((menu) => getCategoryKey(menu) === group.key),
    }))
    .filter((group) => group.items.length > 0);
  const showingFrom = totalItems === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setAvailability("");
    setMinPrice("");
    setMaxPrice("");
    setSort("newest");
    setShowMoreFilters(false);
  };

  const hasEmptyData = !isLoading && !error && (!menus || menus.length === 0);
  const noMatches = !hasEmptyData && !isLoading && totalItems === 0;

  const selectClass =
    "flex-shrink-0 appearance-none border border-gray-300 rounded-md py-2 pl-3 pr-8 leading-relaxed cursor-pointer focus:outline-none bg-white min-w-[150px]";

  return (
    <main className="min-h-screen bg-gray-50">
      <MerchantBanner
        branch={branch}
        merchant={merchant}
        itemCount={menus?.length || 0}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <p className="text-sm font-semibold text-secondary-color">
              Branch #{branchId}
              {branch?.address ? ` · ${branch.address}` : ""}
            </p>
            <h2 className="text-2xl font-bold text-gray-950">Menu items</h2>
          </div>

          {error && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-color px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
        </div>

        {!isLoading && !error && (menus?.length || 0) > 0 && (
          <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setSearch("");
                    }
                  }}
                  placeholder="Search by item name or description"
                  className="pl-10 pr-9 w-full border border-gray-300 rounded-md py-2 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-color/20"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                aria-label="Category filter"
                className="flex-shrink-0 appearance-none border border-gray-300 rounded-md py-2 pl-3 pr-8 leading-relaxed cursor-pointer focus:outline-none bg-white min-w-[170px] max-w-[230px]"
              >
                <option value="">All categories</option>
                {categoryOptions.map((option) => (
                  <option
                    key={
                      option.value === ""
                        ? UNCATEGORIZED_VALUE
                        : option.value
                    }
                    value={option.value === "" ? UNCATEGORIZED_VALUE : option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={availability}
                onChange={(event) => setAvailability(event.target.value)}
                aria-label="Availability filter"
                className={selectClass}
              >
                {AVAILABILITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="relative flex-shrink-0">
                <ArrowUpDown
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  size={16}
                />
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  aria-label="Sort menus"
                  className="flex-shrink-0 appearance-none border border-gray-300 rounded-md py-2 pl-9 pr-8 leading-relaxed cursor-pointer focus:outline-none bg-white min-w-[170px]"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  size={16}
                />
              </div>

              <div className="relative flex-shrink-0" ref={moreFiltersRef}>
                <button
                  type="button"
                  onClick={() => setShowMoreFilters((open) => !open)}
                  className={`flex items-center gap-2 border rounded-md py-2 px-3 leading-relaxed hover:bg-gray-50 ${
                    minPrice || maxPrice
                      ? "border-primary-color text-primary-color"
                      : "border-gray-300 text-gray-700"
                  }`}
                >
                  <Filter size={16} />
                  More filters
                  <ChevronDown size={14} />
                </button>

                {showMoreFilters && (
                  <div className="absolute left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-20 animate-fade-in">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-900">
                        Price range
                      </p>
                      <span className="text-xs text-gray-400">MMK</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={minPrice}
                        onChange={(event) => setMinPrice(event.target.value)}
                        placeholder="Min"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-color/20"
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        type="number"
                        min="0"
                        value={maxPrice}
                        onChange={(event) => setMaxPrice(event.target.value)}
                        placeholder="Max"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-color/20"
                      />
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => {
                          setMinPrice("");
                          setMaxPrice("");
                        }}
                        className="text-sm font-semibold text-primary-color hover:underline"
                      >
                        Reset price
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowMoreFilters(false)}
                        className="text-sm text-gray-500 hover:text-black"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-black"
                >
                  <X size={16} />
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}

        {!isLoading && !error && totalItems > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-5 mb-5">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-900">{showingFrom}</span>{" "}
              to <span className="font-semibold text-gray-900">{showingTo}</span>{" "}
              of <span className="font-semibold text-gray-900">{totalItems}</span>{" "}
              {totalItems === 1 ? "item" : "items"}
            </p>
            <p className="text-sm text-gray-500">
              Page <span className="font-semibold">{safePage}</span> of{" "}
              {totalPages}
            </p>
          </div>
        )}

        {isLoading ? (
          <div>
            <div className="mb-5 inline-flex items-center gap-2 text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading menus
            </div>
            <MenuSkeleton />
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg border border-red-100 p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
            <h3 className="mt-3 text-lg font-bold text-gray-950">
              Unable to load menus
            </h3>
            <p className="mt-2 text-gray-600">{error}</p>
          </div>
        ) : hasEmptyData ? (
          <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
            <SearchX className="w-10 h-10 mx-auto text-gray-400" />
            <h3 className="mt-3 text-lg font-bold text-gray-950">
              No menus available
            </h3>
            <p className="mt-2 text-gray-600">
              This merchant has no active menu items for this branch yet.
            </p>
          </div>
        ) : noMatches ? (
          <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
            <SearchX className="w-10 h-10 mx-auto text-gray-400" />
            <h3 className="mt-3 text-lg font-bold text-gray-950">
              No menus match your filters
            </h3>
            <p className="mt-2 text-gray-600">
              Try a different search term or reset the filters.
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-color px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition"
            >
              <X className="w-4 h-4" />
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {visibleGroups.map((group) => (
              <section key={group.key || "uncategorized"}>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-lg font-bold text-gray-950">
                    {group.label}
                  </h3>
                  <span className="text-xs font-semibold text-gray-600 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1">
                    {group.items.length}{" "}
                    {group.items.length === 1 ? "item" : "items"}
                  </span>
                  <div className="flex-1 border-b border-dashed border-gray-200" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {group.items.map((menu) => (
                    <MenuCard key={menu.id} menu={menu} />
                  ))}
                </div>
              </section>
            ))}

            {totalPages > 1 && (
              <div className="mt-6 bg-white border border-gray-100 rounded-lg px-4 py-3 flex items-center justify-center sm:justify-end shadow-sm overflow-x-auto">
                <MenusPagination
                  page={safePage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default Menus;
